/**
 * Booking context (CORE), the state machine and derived states.
 * Source of truth: docs/domain/03-core-model.md §2 and the state diagram in
 * docs/domain/01-discovery.md §3. This file is PURE (no I/O): the aggregate's
 * time semantics live here and nowhere else (04-architecture.md §6), so every
 * transition and every clock-dependent question is unit-testable.
 *
 * Instants only, never wall-clock. The date+time → instant conversion is done
 * once at request time (Scheduling owns the timezone); by the time a Booking
 * exists its scheduledStart/scheduledEnd are real instants and comparisons are
 * timezone-free.
 */
import type { FailureCode } from "@/modules/shared/failure";

/**
 * Stored state, what sits in `booking.bookings.state`. Two of these
 * (Expired, Completed) are *derived*: they are computed on read and only ever
 * written when a command normalizes a booking it is about to act on
 * (§2 "Stored state may lag effective state"). Reads never write.
 */
export type BookingState =
  | "requested"
  | "approved"
  | "withdrawn"
  | "declined"
  | "expired"
  | "cancelled_by_consumer"
  | "cancelled_by_business"
  | "completed";

/** The five states that never transition again. */
export const TERMINAL_STATES: ReadonlySet<BookingState> = new Set<BookingState>([
  "withdrawn",
  "declined",
  "expired",
  "cancelled_by_consumer",
  "cancelled_by_business",
  "completed",
]);

export function isTerminal(state: BookingState): boolean {
  return TERMINAL_STATES.has(state);
}

/** Policies as value objects (§2 "Policies as values"). Platform-wide in v1. */
export const CancellationPolicy = { windowBeforeMs: 60 * 60 * 1000 } as const; // 1h
export const ReviewUnlockPolicy = { afterMs: 15 * 60 * 1000 } as const; // 15min

/** The minimum a booking must expose for the pure time functions. */
export interface BookingTimes {
  state: BookingState;
  scheduledStart: Date;
  scheduledEnd: Date;
}

/**
 * The single pure function that answers every time-dependent question
 * (03-core-model.md §2, "Derived-state evaluation"):
 *   Requested and now ≥ start → Expired
 *   Approved  and now ≥ end   → Completed
 *   otherwise                 → stored state
 */
export function effectiveState(booking: BookingTimes, now: Date): BookingState {
  const t = now.getTime();
  if (booking.state === "requested" && t >= booking.scheduledStart.getTime()) {
    return "expired";
  }
  if (booking.state === "approved" && t >= booking.scheduledEnd.getTime()) {
    return "completed";
  }
  return booking.state;
}

/**
 * Cancellation Window (H10-area, discovery glossary): a Consumer may cancel an
 * Approved booking only strictly before `start − 1h`. The window is inclusive
 * at its edge, at exactly `start − 1h` cancellation is already blocked
 * (test #2). Measured against the *current* start, so a move re-opens it.
 */
export function canCancel(booking: BookingTimes, now: Date): boolean {
  if (effectiveState(booking, now) !== "approved") return false;
  const deadline =
    booking.scheduledStart.getTime() - CancellationPolicy.windowBeforeMs;
  return now.getTime() < deadline;
}

/**
 * Review Unlock (H13): 15 minutes after the scheduled end of a booking that
 * reached Completed. Once unlocked it never closes.
 */
export function canReview(booking: BookingTimes, now: Date): boolean {
  if (effectiveState(booking, now) !== "completed") return false;
  return now.getTime() >= booking.scheduledEnd.getTime() + ReviewUnlockPolicy.afterMs;
}

/**
 * Transition guards. Each answers "may this actor make this move *now*?" from
 * the effective state alone; guards that also depend on capacity or on the
 * block still existing (approve/move) are completed in the data layer under a
 * row lock: this is the state half of those checks.
 */
/**
 * A guard's refusal is a **code**, not a sentence (bM1). Drawn from the shared
 * `FailureCode` union via `Extract`, so the two can never drift: renaming a
 * code there turns this into `never` and the build stops.
 */
export type GuardCode = Extract<
  FailureCode,
  | "booking_not_open"
  | "booking_expired"
  | "booking_not_active"
  | "cancellation_window"
  | "booking_already_complete"
  // R21: the Consumer's own change of time (H50) and the two answers to the
  // clinic's suggested one (H49).
  | "reschedule_window"
  | "no_pending_suggestion"
  | "suggestion_expired"
>;

export type GuardResult = { ok: true } | { ok: false; reason: GuardCode };

const OK: GuardResult = { ok: true };

/**
 * The **web's** copy for each guard code, and the single source of truth for
 * the sentences the screen docs quote. Other clients render their own words
 * from the same codes; that is the whole reason the guard stopped returning a
 * sentence (docs/mobile/api-build-plan.md bM1).
 */
export const GUARD_COPY: Record<GuardCode, string> = {
  booking_not_open: "This request is no longer open.",
  booking_expired: "This request has expired. Its start time has passed.",
  booking_not_active: "This booking is not active.",
  cancellation_window:
    "Cancellations close 1 hour before the start. Please contact the business directly.",
  booking_already_complete: "This booking has already taken place.",
  // The same 1-hour policy, said for the act it is refusing. "Cancellations
  // close" on a screen whose button reads "Change time" is the app answering a
  // question nobody asked.
  reschedule_window:
    "Changes close 1 hour before the start. Please contact the business directly.",
  no_pending_suggestion: "There is no suggested time waiting on this booking.",
  suggestion_expired: "That suggested time has passed. Ask the clinic for another.",
};

/** Requested → Withdrawn (Consumer, anytime while Requested, H8). */
export function guardWithdraw(booking: BookingTimes, now: Date): GuardResult {
  const state = effectiveState(booking, now);
  if (state === "expired") return { ok: false, reason: "booking_expired" };
  if (state !== "requested") return { ok: false, reason: "booking_not_open" };
  return OK;
}

/** Requested → Declined (Business, anytime while Requested, H2). */
export function guardDecline(booking: BookingTimes, now: Date): GuardResult {
  const state = effectiveState(booking, now);
  if (state === "expired") return { ok: false, reason: "booking_expired" };
  if (state !== "requested") return { ok: false, reason: "booking_not_open" };
  return OK;
}

/**
 * Requested → Approved (Business). State half only: the booking must still be
 * an open Request. A request whose start has passed is Expired and can never
 * be approved (test #3). Capacity and the removed-block guard (H16) are checked
 * under the occupancy lock in the data layer.
 */
export function guardApprove(booking: BookingTimes, now: Date): GuardResult {
  const state = effectiveState(booking, now);
  if (state === "expired") return { ok: false, reason: "booking_expired" };
  if (state !== "requested") return { ok: false, reason: "booking_not_open" };
  return OK;
}

/** Approved → CancelledByConsumer (blocked inside the 1-hour Window). */
export function guardConsumerCancel(booking: BookingTimes, now: Date): GuardResult {
  const state = effectiveState(booking, now);
  if (state === "completed") return { ok: false, reason: "booking_already_complete" };
  if (state !== "approved") return { ok: false, reason: "booking_not_active" };
  if (!canCancel(booking, now)) return { ok: false, reason: "cancellation_window" };
  return OK;
}

/** Approved → CancelledByBusiness (always allowed while Approved, H2). */
export function guardBusinessCancel(booking: BookingTimes, now: Date): GuardResult {
  const state = effectiveState(booking, now);
  if (state === "completed") return { ok: false, reason: "booking_already_complete" };
  if (state !== "approved") return { ok: false, reason: "booking_not_active" };
  return OK;
}

/**
 * Approved → Approved (Moved). State half only: must be Approved and not yet
 * Completed. The target block's capacity + existence are checked under the
 * occupancy lock in the data layer, atomically with the occupancy swap.
 *
 * Since H49 the **business** no longer reaches this transition directly: it
 * suggests a time and the Consumer accepts. This guard is what both remaining
 * paths into a move share, the Consumer's acceptance of a suggestion (H49) and
 * the Consumer's own change of time (H50), which adds the window on top.
 */
export function guardMove(booking: BookingTimes, now: Date): GuardResult {
  const state = effectiveState(booking, now);
  if (state === "completed") return { ok: false, reason: "booking_already_complete" };
  if (state !== "approved") return { ok: false, reason: "booking_not_active" };
  return OK;
}

/**
 * The Consumer's own change of time (H50), the mirror of `canCancel`.
 *
 * **The same 1-hour policy object**, deliberately: a change of time is a
 * cancellation of one hour and a booking of another, so letting somebody move
 * an appointment at ten to nine while refusing to let them cancel it would be
 * two rules for one fact. Measured against the *current* start, so an accepted
 * suggestion re-opens the window exactly as a move always has.
 */
export function canReschedule(booking: BookingTimes, now: Date): boolean {
  return canCancel(booking, now);
}

/** Approved → Approved (Moved by the Consumer, H50). */
export function guardConsumerReschedule(
  booking: BookingTimes,
  now: Date,
): GuardResult {
  const state = effectiveState(booking, now);
  if (state === "completed") return { ok: false, reason: "booking_already_complete" };
  if (state !== "approved") return { ok: false, reason: "booking_not_active" };
  if (!canReschedule(booking, now)) {
    return { ok: false, reason: "reschedule_window" };
  }
  return OK;
}
